import { Box, Button, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { TripResponse } from 'store/trips/trips.types'
import {
  ItemContainer,
  Name,
  NameContainer,
  TripId,
} from './trip-selection-list.styles'
import { useDispatch, useSelector } from 'react-redux'
import {
  addDrivers,
  addFirstDriver,
  addSecondDriver,
  clearTripPayload,
  loadTripForEdit,
  setSelectedTrip,
} from 'store/trips/trips.slice'
import { RootState } from 'store'
import { MouseEvent, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import { useDeleteTripMutation, useEndTripMutation } from 'store/trips/trips.api'
import { useListDriversQuery } from 'store/drivers/drivers.api'
import { DriverFixture } from 'store/trips/trips.fixtures'
import { getUserId } from 'utils/common'
import CreateTripDialog from 'pages/trip/create-trip/create-trip-dialog'
import CreateCarrierTripDialog from 'pages/carrier-trip/create-carrier-trip-dialog'

export type TripCardVariant = 'driver' | 'carrier'

interface TripSelectionListProps {
  trips: TripResponse[] | undefined
  variant?: TripCardVariant
  onTripsChanged?: () => void
}

interface ItemProps {
  trip: TripResponse
  variant: TripCardVariant
  onTripsChanged?: () => void
}

const actionButtonSx = {
  flex: 1,
  minWidth: 0,
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  paddingX: '10px',
  paddingY: '6px',
} as const

const Item = ({ trip, variant, onTripsChanged }: ItemProps) => {
  const dispatch = useDispatch()
  const userId = getUserId()
  const [editTripOpen, setEditTripOpen] = useState(false)
  const [endOpen, setEndOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const selectedTrip = useSelector(
    (state: RootState) => state.trips.selectedTrip
  )

  const { data: drivers } = useListDriversQuery(
    { userId, driverAppStatus: 'ACTIVATED' },
    { skip: !userId || variant !== 'driver' }
  )

  const [endTrip] = useEndTripMutation()
  const [deleteTrip] = useDeleteTripMutation()

  const isSelected = useMemo(
    () => selectedTrip?.tripId === trip?.tripId,
    [selectedTrip, trip]
  )

  const isActive = trip?.status === 'ACTIVE'
  const showDelete = !isActive && variant === 'driver'

  const displayCreatedDate = useMemo(() => {
    if (!trip?.createdDate) return ''
    const dt = DateTime.fromISO(trip.createdDate, { zone: 'utc' }).toLocal()
    if (!dt.isValid) return ''
    return dt.toFormat('LLL dd, yyyy')
  }, [trip?.createdDate])

  const handleTripSelection = () => {
    dispatch(setSelectedTrip({ trip }))
  }

  const handleOpenEditTrip = (e: MouseEvent) => {
    e.stopPropagation()
    dispatch(setSelectedTrip({ trip }))
    dispatch(loadTripForEdit({ trip }))

    if (variant === 'driver') {
      const first =
        drivers?.find((d) => d.id === trip.firstDriverId) ?? DriverFixture

      dispatch(addFirstDriver(first))
      dispatch(addSecondDriver(DriverFixture))
      dispatch(addDrivers([first, DriverFixture]))
    }

    setEditTripOpen(true)
  }

  const handleCloseEditTrip = () => {
    dispatch(clearTripPayload())
    setEditTripOpen(false)
  }

  const handleEndTrip = async () => {
    try {
      await endTrip({ tripId: trip?.tripId?.toString() ?? '' }).unwrap()
      setEndOpen(false)
      onTripsChanged?.()
    } catch (error) {
      console.error('Error on end trip', error)
    }
  }

  const handleDeleteTrip = async () => {
    try {
      await deleteTrip({ tripId: trip?.tripId?.toString() ?? '' }).unwrap()
      setDeleteOpen(false)
      onTripsChanged?.()
    } catch (error) {
      console.error('Error while deleting trip', error)
    }
  }

  const actionButtonRowSx = {
    padding: '8px 12px 12px',
    bgcolor: isSelected ? '#E8F3FC' : 'common.white',
  } as const

  const assigneeLabel = variant === 'carrier' ? 'carrier' : 'driver'

  return (
    <ItemContainer $selected={isSelected} onClick={handleTripSelection}>
      <NameContainer $selected={isSelected}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Name>{trip?.firstDriverName}</Name>
          <TripId $selected={isSelected}>{trip?.userTripId}</TripId>
        </Stack>
        {displayCreatedDate && (
          <TripId $selected={isSelected}>Created Date: {displayCreatedDate}</TripId>
        )}
      </NameContainer>
      {(isActive || showDelete) && (
        <Stack
          direction="row"
          spacing={1}
          sx={actionButtonRowSx}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="outlined"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleOpenEditTrip}
            sx={{
              ...actionButtonSx,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Edit Trip
          </Button>
          {isActive ? (
            <Button
              variant="outlined"
              startIcon={<TaskAltIcon />}
              onClick={() => setEndOpen(true)}
              sx={{
                ...actionButtonSx,
                borderColor: 'grey.600',
                color: 'grey.700',
                '&:hover': {
                  borderColor: 'grey.700',
                  bgcolor: 'rgba(97, 97, 97, 0.04)',
                },
              }}
            >
              Complete Trip
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteOpen(true)}
              sx={actionButtonSx}
            >
              Delete Trip
            </Button>
          )}
        </Stack>
      )}
      {editTripOpen && variant === 'driver' && (
        <CreateTripDialog
          open={editTripOpen}
          onClose={handleCloseEditTrip}
          mode="edit"
          editingTripId={trip.tripId}
          onSaved={() => {
            onTripsChanged?.()
            handleCloseEditTrip()
          }}
        />
      )}
      {editTripOpen && variant === 'carrier' && (
        <CreateCarrierTripDialog
          open={editTripOpen}
          onClose={handleCloseEditTrip}
          mode="edit"
          editingTripId={trip.tripId}
          onSaved={() => {
            onTripsChanged?.()
            handleCloseEditTrip()
          }}
        />
      )}
      <ConfirmationDialog
        title="Complete Trip"
        open={endOpen}
        handleClose={() => setEndOpen(false)}
        successBtnText="Complete Trip"
        successBtnTextStyle={{
          sx: {
            bgcolor: 'grey.600',
            color: 'common.white',
            '&:hover': { bgcolor: 'grey.700' },
          },
        }}
        handleSuccessBtn={handleEndTrip}
      >
        Once you complete the trip, you cannot assign these order events to
        another {assigneeLabel}. The trip will be fully completed.
      </ConfirmationDialog>
      <ConfirmationDialog
        title="Delete Trip"
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        successBtnText="Delete"
        successBtnTextStyle={{ color: 'error' }}
        handleSuccessBtn={handleDeleteTrip}
      >
        Are you sure to delete the trip?
      </ConfirmationDialog>
    </ItemContainer>
  )
}

const TripSelectionList = ({
  trips,
  variant = 'driver',
  onTripsChanged,
}: TripSelectionListProps) => {
  if (trips?.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          No trips found!!
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {trips?.map((trip) => (
        <Item
          key={trip?.tripId}
          trip={trip}
          variant={variant}
          onTripsChanged={onTripsChanged}
        />
      ))}
    </>
  )
}

export default TripSelectionList
